import { Box, useMergeRefs } from '@chakra-ui/react';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useStableCallback } from '@main/shared/browser';
import {
  chakraComponents,
  GetOptionValue,
  GroupBase,
  MultiValueProps,
  MultiValueRemoveProps,
  Props,
  SelectComponentsConfig,
  SelectInstance,
} from 'chakra-react-select';
import {
  ComponentType,
  ForwardedRef,
  forwardRef,
  memo,
  MouseEvent,
  PointerEvent,
  useMemo,
  useRef,
} from 'react';

const DND_KIT_DOCUMENT_LISTENER_CLEANUP_DELAY_MS = 50;
type InputSelection = { start: number | null; end: number | null };

export type PatchedSelectProps<TProps> = TProps & {
  onOptionReorder?: SelectOptionReorder;
};
export type SelectOptionReorder = (source: string, destination: string) => void;

export function patchSelect<TSelect extends ComponentType<Props>>(component: TSelect): TSelect {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
  const Select = component as any;

  return memo(
    forwardRef(
      (
        { onOptionReorder, ...props }: PatchedSelectProps<Props>,
        ref: ForwardedRef<SelectInstance<unknown, boolean, GroupBase<unknown>>>,
      ) => {
        const selectRef = useRef<SelectInstance<unknown, boolean, GroupBase<unknown>>>(null);
        const inputSelectionRef = useRef<InputSelection | null>(null);
        const mergedRef = useMergeRefs(selectRef, ref);
        const getOptionValue = useStableCallback(props.getOptionValue);
        const captureInputSelection = useStableCallback(() => {
          const input = selectRef.current?.inputRef;
          inputSelectionRef.current =
            input && document.activeElement === input
              ? { start: input.selectionStart, end: input.selectionEnd }
              : null;
        });
        const MultiValue = useMemo(
          () =>
            patchMultiValue(props.components?.MultiValue, getOptionValue, captureInputSelection),
          [props.components?.MultiValue, getOptionValue, captureInputSelection],
        );
        const MultiValueRemove = useMemo(
          () => patchMultiValueRemove(props.components?.MultiValueRemove),
          [props.components?.MultiValueRemove],
        );
        const items = useMemo(
          () => (Array.isArray(props.value) ? (props.value?.map(getOptionValue) ?? []) : []),
          [props.value, getOptionValue],
        );

        function restoreInputCaret(event: DragEndEvent) {
          if (event.activatorEvent instanceof KeyboardEvent) {
            return;
          }

          const inputSelection = inputSelectionRef.current;
          inputSelectionRef.current = null;

          // dnd-kit clears document selection while pointer-dragging and keeps
          // its selectionchange listener for 50ms after drop. In Chromium that
          // removes the input's native caret without changing activeElement.
          setTimeout(() => {
            const currentInput = selectRef.current?.inputRef;
            if (!currentInput) {
              return;
            }

            currentInput.focus({ preventScroll: true });
            const fallbackPosition = currentInput.value.length;
            currentInput.setSelectionRange(
              inputSelection?.start ?? fallbackPosition,
              inputSelection?.end ?? fallbackPosition,
            );
          }, DND_KIT_DOCUMENT_LISTENER_CLEANUP_DELAY_MS);
        }

        function onDragEnd(event: DragEndEvent) {
          restoreInputCaret(event);

          if (!event.over || event.active.id === event.over.id) {
            return;
          }
          onOptionReorder?.(event.active.id as string, event.over.id as string);
        }

        if (!onOptionReorder) {
          return <Select {...props} ref={ref} />;
        }

        if (!props.getOptionValue) {
          throw new Error('getOptionValue is required when using onOptionReorder');
        }

        return (
          <DndContext
            modifiers={[restrictToParentElement]}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            onDragCancel={restoreInputCaret}
          >
            <SortableContext items={items}>
              <Select
                {...props}
                components={{ ...props.components, MultiValue, MultiValueRemove }}
                ref={mergedRef}
              />
            </SortableContext>
          </DndContext>
        );
      },
    ),
  ) as unknown as TSelect;
}

type MultiValueComponent = SelectComponentsConfig<
  unknown,
  boolean,
  GroupBase<unknown>
>['MultiValue'];

function patchMultiValue<TComponent extends MultiValueComponent>(
  component: TComponent,
  getOptionValue: GetOptionValue<unknown>,
  captureInputSelection: () => void,
): TComponent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
  const MultiValue = (component ?? chakraComponents.MultiValue) as any;

  return memo((props: MultiValueProps<unknown, true>) => {
    const { attributes, listeners, transform, transition, isDragging, setNodeRef } = useSortable({
      id: getOptionValue(props.data),
    });

    function onMouseDown(e: MouseEvent<HTMLDivElement>) {
      e.preventDefault();
      e.stopPropagation();
      props.innerProps?.onMouseDown?.(e);
    }

    function onPointerDown(e: PointerEvent<HTMLDivElement>) {
      e.stopPropagation();
      props.removeProps?.onPointerDown?.(e);
    }

    return (
      <Box
        {...(props.selectProps.menuIsOpen && listeners)}
        {...attributes}
        onPointerDownCapture={props.selectProps.menuIsOpen ? captureInputSelection : undefined}
        display="inline-flex"
        alignItems="center"
        lineHeight={0}
        transform={CSS.Translate.toString(transform)}
        transition={transition}
        cursor={isDragging ? 'grabbing' : props.selectProps.menuIsOpen ? 'grab' : undefined}
        ref={setNodeRef}
      >
        <MultiValue
          {...props}
          innerProps={{ ...props.innerProps, ...(props.selectProps.menuIsOpen && { onMouseDown }) }}
          removeProps={{ ...props.removeProps, onPointerDown }}
        />
      </Box>
    );
  }) as unknown as TComponent;
}

type MultiValueRemoveComponent = SelectComponentsConfig<
  unknown,
  boolean,
  GroupBase<unknown>
>['MultiValueRemove'];

function patchMultiValueRemove<TComponent extends MultiValueRemoveComponent>(
  component: TComponent,
): TComponent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
  const MultiValueRemove = (component ?? chakraComponents.MultiValueRemove) as any;

  return memo((props: MultiValueRemoveProps<unknown, true>) => {
    function onPointerDown(e: PointerEvent<HTMLDivElement>) {
      e.stopPropagation();
      props.innerProps?.onPointerDown?.(e);
    }

    return <MultiValueRemove {...props} innerProps={{ ...props.innerProps, onPointerDown }} />;
  }) as unknown as TComponent;
}
